/* istanbul ignore file */
import * as React from "react";
import * as ReactDOM from "react-dom";
import { isFunction, dynamicScriptLoad } from "sb-helpers";
import { BaseBlock } from "../BaseBlock";
var AsyncBlockDownloadStatuses;
(function (AsyncBlockDownloadStatuses) {
    AsyncBlockDownloadStatuses["success"] = "0";
    AsyncBlockDownloadStatuses["fail"] = "1";
    AsyncBlockDownloadStatuses["pending"] = "2";
})(AsyncBlockDownloadStatuses || (AsyncBlockDownloadStatuses = {}));
/**
 * AsyncBlockInit provides dummy block instance that can handle asyncronous loading of
 * JavaScript artifacts and init any Responsive or React block before its source was
 * downloaded on the page.
 */
export class AsyncBlockInit extends BaseBlock {
    /**
     * Constructor
     * @param {SBMsgBus} bus used for messages exchange
     * @param {String} blockClassName Unique name of the block constructor function or class
     * @param {String} domId unique ID used for identifying correct DOM element and message bus topic for the instance
     * @param {AsyncBlockOptions} config Extended config
     * @param {Any} rest Any other parametters passed to the constructor are stored and passed on during init
     */
    constructor(bus, blockClassName, domId, config, ...rest) {
        super(domId, config);
        this.block;
        this.bus = bus;
        this.rest = rest;
        this.panelId = "";
        this.domId = domId;
        this.config = config;
        this.class = blockClassName;
        this.paramsToSet = undefined;
        this.uniqueTopic = `${this.bus.sbPrefix}${domId}`;
        this.classTopic = `${this.bus.sbPrefix}${blockClassName}_loaded`;
        this.instanceTopic = `${this.uniqueTopic}_loaded`;
        this.loaded = this.loaded.bind(this);
        this.busMsgRouter = this.busMsgRouter.bind(this);
        this.isCodeLoaded = Array.isArray(config.requires) === true ?
            AsyncBlockDownloadStatuses.pending :
            AsyncBlockDownloadStatuses.success; // no files - trigger the success flow
        if (this.isLoaded) {
            this.loaded(true);
        }
        else {
            this.bus.subscribe(`${this.classTopic}`, this.loaded);
        }
        this.isDeactivated = false;
    }
    get isLoaded() {
        return typeof window[this.class] === "function";
    }
    /**
     * membersRemap, execute once method that links between members of the real block and dummy block
     */
    /* tslint:disable */
    membersRemap() {
        // defensive coding for smartasses
        if (this.isLoaded) {
            // compatibility with mobile app
            const legacyCompatibalityProps = { PanelID: "panelId", parentBlock: "ParentBlock" };
            Object.keys(legacyCompatibalityProps).forEach(x => Object.defineProperty(this.block, x, {
                get: () => this[legacyCompatibalityProps[x]],
                set: y => this[legacyCompatibalityProps[x]] = y
            }));
            const protoProps = Object.getOwnPropertyNames(Object.getPrototypeOf(this.block));
            const ownProps = Object.getOwnPropertyNames(this.block);
            const props = [...protoProps, ...ownProps];
            for (let m of props) {
                if (m !== "activate" &&
                    m !== "setParams" &&
                    m !== "destroy") {
                    if (typeof this.block[m] === "function") {
                        this[m] = this.block[m];
                    }
                    else {
                        Object.defineProperty(this, m, {
                            get: () => {
                                return this.block[m];
                            },
                            set: x => this.block[m] = x
                        });
                    }
                }
            }
            // You better lose yourself in the music, the moment
            // You own it, you better never let it go
            // You only get one shot, do not miss your chance to blow
            // This opportunity comes once in a lifetime
            this.membersRemap = () => undefined;
        }
        return undefined;
    }
    /* tslint:enable */
    /**
     * loaded, execute once method that creates instance of the real block after its code was downloaded on the page
     * @param {Boolean} x received as message from the block that loaded
     */
    loaded(x) {
        if (this.block) {
            this.loaded = () => this;
            return this;
        }
        if (x === true && this.isLoaded) {
            this.isCodeLoaded = AsyncBlockDownloadStatuses.success;
            try {
                this.block = new window[this.class](this.domId, this.config, ...this.rest);
            }
            catch (up) {
                throw up;
            }
            this.membersRemap();
            this.bus.unsubscribe(`${this.classTopic}`, this.loaded);
            this.bus.subscribe(`${this.uniqueTopic}`, this.busMsgRouter);
            this.bus.post({ [`${this.instanceTopic}`]: true });
        }
        return this;
    }
    /**
     * busMsgRouter, routes any async messages sent to the block togher with any data sent
     * @param {AsyncBlockTopic} x parametters for the method called via sbMsgBus.post(...)
     */
    busMsgRouter(x) {
        x && Object.keys(x).forEach(y => {
            if (this.isLoaded && isFunction(this.block[y])) {
                try {
                    !this.isDeactivated && this[y](x[y]);
                }
                catch (e) {
                    console.error(e);
                }
            }
        });
    }
    /**
     * destroy, calls super.destroy(), then unsubscribes from the bus leaving the block dead
     */
    destroy() {
        super.destroy();
        this.bus.unsubscribe(`${this.uniqueTopic}`, this.busMsgRouter);
        return this;
    }
    /**
     * setParams, proxy of block.setParams, that works in async way. Stores params in order to pass them
     * to the block once it is downloaded and its instance is created
     * @param {Any} x params to proxy
     */
    setParams(...x) {
        if (this.block) {
            return this.block.setParams(...x);
        }
        else {
            this.paramsToSet = x;
        }
        return this;
    }
    /**
     * activate, async version of the activate method available to the application before the
     * real block is downloaded and init. If called directly it is going to post message to
     * to its own topic.
     * @param {Array<any>} x params to proxy
     */
    /* tslint:disable */
    activate(...x) {
        if (this.isCodeLoaded === AsyncBlockDownloadStatuses.pending) {
            dynamicScriptLoad(this.config.requires || [], () => this.isCodeLoaded = AsyncBlockDownloadStatuses.success, e => {
                this.isCodeLoaded = AsyncBlockDownloadStatuses.fail;
                this.bus.unsubscribe(`${this.uniqueTopic}`, this.busMsgRouter);
                throw new Error(`Block "${this.class}" download failed! With error: ${e.toString()}`);
            });
        }
        if (this.isLoaded && this.isCodeLoaded === AsyncBlockDownloadStatuses.success) {
            if (this.paramsToSet) {
                this.block.setParams(...this.paramsToSet);
                this.paramsToSet = undefined;
            }
            return this.block.activate(...x);
        }
        else {
            this.bus.post({ [this.uniqueTopic]: { activate: x } });
            try {
                this.renderLoader();
            }
            catch (up) {
                throw up;
            }
        }
        this.isDeactivated = false;
        return this;
    }
    /* tslint:enable */
    deactivate() {
        this.isDeactivated = true;
        return super.deactivate();
    }
    renderLoader() {
        const domRef = document.getElementById(this.domId);
        if (!domRef) {
            throw new Error(`Element with ID ${this.domId} does not exist.`);
        }
        else {
            // TODO: consume the loader in a smarter way
            ReactDOM.render((React.createElement("div", { className: "rj-loader" },
                React.createElement("div", { className: "rj-loader-spinner rj-loader-container" },
                    React.createElement("div", { className: "rj-loader-spinner rj-loader-fill" },
                        React.createElement("div", { className: "rj-loader-clipper left" },
                            React.createElement("div", { className: "rj-loader-circle" })),
                        React.createElement("div", { className: "rj-loader-gap-patch" },
                            React.createElement("div", { className: "rj-loader-circle" })),
                        React.createElement("div", { className: "rj-loader-clipper right" },
                            React.createElement("div", { className: "rj-loader-circle" })))))), domRef);
        }
    }
}
//# sourceMappingURL=AsyncBlockInit.js.map